import {
  InvoiceWithPostings,
  PostingWithAmountSelected,
  Transaction,
  TransactionWithAmountLinked,
  Invoice,
  TransactionLink,
  type MutationStatus,
  type TransactionsUpdatedStatus,
  type ReconcileTransactionWithInvoicesInput,
  changeTransactionLinks as changeTransactionLinksMutation,
  reconcileTransactionWithTransaction as reconcileTransactionWithTransactionMutation,
  reconcileTransactionWithInvoices as reconcileTransactionWithInvoicesMutation,
  reconcileInvoicesWithCreditNotes as reconcileInvoicesWithCreditNotesMutation,
  reconcilePendingBalanceWithInvoices as reconcilePendingBalanceWithInvoicesMutation,
  reverseTransaction as reverseTransactionMutation,
  snoozeTransaction as snoozeTransactionMutation,
  unreconcileTransactionOrInvoices as unreconcileTransactionOrInvoicesMutation,
  writeOffPendingBalance as writeOffPendingBalanceMutation,
  editPrivateAccountTransactionAmount as editPrivateAccountTransactionAmountMutation,
  TransactionExclusionReason,
  TransactionStatus,
} from '@rentguru/commons-utils';
import { mutation } from '@up2rent/graphql';
import isNil from 'lodash/isNil';
import { createContext, useContext, ReactNode } from 'react';

import {
  fetchTransactionsWithAllPostings,
  withFreshOffsetCounterparts,
} from 'src/components/Accounting/Transactions/Reconciliation/leaseReconciliationData';

import {
  forgetPendingDecisionId,
  readPendingDecisionId,
  storePendingDecisionId,
} from '../utils/reconciliationDecisionCache';

import { useUser } from './UserContext';

const buildReconciliationAttemptKey = (
  transactionId: string,
  postingsSelected: Pick<PostingWithAmountSelected, 'id' | 'amountSelected'>[],
  invoicesToReconcile: string[],
  amountToPutInPendingBalance?: number
) =>
  [
    transactionId,
    postingsSelected
      .map(({ id, amountSelected }) => `${id}:${amountSelected}`)
      .sort()
      .join(','),
    [...invoicesToReconcile].sort().join(','),
    amountToPutInPendingBalance ?? 0,
  ].join('|');

const buildUnreconciliationAttemptKey = (
  transactionId: string,
  originalTransactions: Transaction[],
  transactionInvoices?: Invoice[],
  leaseId?: string
) =>
  [
    transactionId,
    originalTransactions
      .map(({ id }) => id)
      .sort()
      .join(','),
    (transactionInvoices ?? [])
      .map(({ id }) => id)
      .sort()
      .join(','),
    leaseId ?? '',
  ].join('|');

export interface TransactionExclusionDetails {
  exclusionReason: TransactionExclusionReason;
  exclusionComment?: string;
  rememberExclusion?: boolean;
}

export interface ReconciliationContextType {
  changeTransactionLinks: (
    transactionId: string,
    newTransactionLinks: TransactionLink[],
    newStatus?: TransactionStatus,
    exclusion?: TransactionExclusionDetails,
    expectedVersion?: number
  ) => Promise<boolean>;
  reconcileTransactionWithTransaction: (
    positiveTransaction: TransactionWithAmountLinked,
    negativeTransaction: TransactionWithAmountLinked,
    leaseId: string,
    invoiceSenderId: string
  ) => Promise<boolean>;
  reconcileTransactionWithInvoices: (
    transactionSelected: Pick<TransactionWithAmountLinked, 'id' | 'amountLinked' | 'amountToBeReconciled'>,
    postingsSelected: Pick<PostingWithAmountSelected, 'id' | 'amountSelected'>[],
    invoicesToReconcile: string[],
    amountToPutInPendingBalance?: number
  ) => Promise<boolean>;
  reconcileInvoicesWithCreditNotes: (
    bankAccountId: string,
    postingsSelected: PostingWithAmountSelected[],
    leaseId: string,
    invoicesToReconcile: InvoiceWithPostings[]
  ) => Promise<boolean>;
  reconcilePendingBalanceWithInvoices: (
    leaseId: string,
    invoicesToReconcile: InvoiceWithPostings[],
    postingsSelected: PostingWithAmountSelected[],
    allTransactionsOfLease: TransactionWithAmountLinked[]
  ) => Promise<boolean>;
  reverseTransaction: (transactionId: string) => Promise<boolean>;
  snoozeTransaction: (transactionId: string, snoozedUntil: string | null) => Promise<boolean>;
  unreconcileTransactionOrInvoices: (
    transaction: Transaction,
    originalTransactions: Transaction[],
    transactionInvoices?: Invoice[],
    leaseId?: string
  ) => Promise<boolean>;
  writeOffPendingBalance: (leaseId: string) => Promise<boolean>;
  editPrivateAccountTransactionAmount: (transactionId: string, invoiceId: string, amount: number) => Promise<boolean>;
}

const ReconciliationContext = createContext<ReconciliationContextType | undefined>(undefined);

export const ReconciliationContextProvider = ({ children }: { children: ReactNode }) => {
  const takeDecisionId = (attemptKey: string) => {
    const pendingDecisionId = readPendingDecisionId(attemptKey);
    if (!isNil(pendingDecisionId)) {
      return pendingDecisionId;
    }
    const decisionId = crypto.randomUUID();
    storePendingDecisionId(attemptKey, decisionId);
    return decisionId;
  };
  const { clientContactId } = useUser();

  const changeTransactionLinks = async (
    transactionId: string,
    newTransactionLinks: TransactionLink[],
    newStatus?: TransactionStatus,
    exclusion?: TransactionExclusionDetails,
    expectedVersion?: number
  ) => {
    const attemptKey = [
      transactionId,
      JSON.stringify(newTransactionLinks),
      newStatus ?? '',
      exclusion?.exclusionReason ?? '',
      exclusion?.exclusionComment ?? '',
      exclusion?.rememberExclusion ? 'remember' : '',
      expectedVersion ?? '',
    ].join('|');
    try {
      const result = await mutation<TransactionsUpdatedStatus>(changeTransactionLinksMutation, {
        input: {
          transactionId,
          newTransactionLinks: JSON.stringify(newTransactionLinks),
          clientContactId,
          newStatus,
          ...exclusion,
          expectedVersion,
          decisionId: takeDecisionId(attemptKey),
        },
      });
      if (result.status) {
        forgetPendingDecisionId(attemptKey);
      }
      return result.status ?? false;
    } catch (error) {
      console.error('Error changing transaction links', error);
      return false;
    }
  };

  const reconcileTransactionWithTransaction = async (
    positiveTransaction: TransactionWithAmountLinked,
    negativeTransaction: TransactionWithAmountLinked,
    leaseId: string,
    invoiceSenderId: string
  ) => {
    const attemptKey = [positiveTransaction.id, negativeTransaction.id, leaseId].join('|');
    try {
      const [freshPositiveTransaction, freshNegativeTransaction] = await fetchTransactionsWithAllPostings([
        positiveTransaction,
        negativeTransaction,
      ]);
      const result = await mutation<MutationStatus>(reconcileTransactionWithTransactionMutation, {
        input: {
          positiveTransaction: JSON.stringify(freshPositiveTransaction),
          negativeTransaction: JSON.stringify(freshNegativeTransaction),
          leaseId,
          invoiceSenderId,
          clientContactId,
          decisionId: takeDecisionId(attemptKey),
        },
      });
      if (result.status) {
        forgetPendingDecisionId(attemptKey);
      }
      return result.status ?? false;
    } catch (error) {
      console.error('Error reconciling transaction with transaction', error);
      return false;
    }
  };

  const reconcileTransactionWithInvoices = async (
    transactionSelected: Pick<TransactionWithAmountLinked, 'id' | 'amountLinked' | 'amountToBeReconciled'>,
    postingsSelected: Pick<PostingWithAmountSelected, 'id' | 'amountSelected'>[],
    invoicesToReconcile: string[],
    amountToPutInPendingBalance?: number
  ) => {
    if (isNil(clientContactId)) {
      return false;
    }
    const attemptKey = buildReconciliationAttemptKey(
      transactionSelected.id,
      postingsSelected,
      invoicesToReconcile,
      amountToPutInPendingBalance
    );
    try {
      const input: ReconcileTransactionWithInvoicesInput = {
        transactionSelected: JSON.stringify({
          id: transactionSelected.id,
          amountLinked: transactionSelected.amountLinked,
          amountToBeReconciled: transactionSelected.amountToBeReconciled,
        }),
        postingsSelected: postingsSelected.map(({ id, amountSelected }) => JSON.stringify({ id, amountSelected })),
        invoicesToReconcile,
        amountToPutInPendingBalance,
        clientContactId,
        decisionId: takeDecisionId(attemptKey),
      };
      const result = await mutation<MutationStatus>(reconcileTransactionWithInvoicesMutation, {
        input,
      });
      if (result.status) {
        forgetPendingDecisionId(attemptKey);
      }
      return result.status ?? false;
    } catch (error) {
      console.error('Error reconciling transaction with invoices', error);
      return false;
    }
  };

  const reconcileInvoicesWithCreditNotes = async (
    bankAccountId: string,
    postingsSelected: PostingWithAmountSelected[],
    leaseId: string,
    invoicesToReconcile: InvoiceWithPostings[]
  ) => {
    try {
      const result = await mutation<MutationStatus>(reconcileInvoicesWithCreditNotesMutation, {
        input: {
          bankAccountId,
          postingsSelected: JSON.stringify(postingsSelected.map(({ id, amountSelected }) => ({ id, amountSelected }))),
          invoicesToReconcile: invoicesToReconcile.map((invoice) => invoice.id),
          leaseId,
          clientContactId,
        },
      });
      return result.status ?? false;
    } catch (error) {
      console.error('Error reconciling invoices with credit notes', error);
      return false;
    }
  };

  const reconcilePendingBalanceWithInvoices = async (
    leaseId: string,
    invoicesToReconcile: InvoiceWithPostings[],
    postingsSelected: PostingWithAmountSelected[],
    allTransactionsOfLease: TransactionWithAmountLinked[]
  ) => {
    const invoiceIds = invoicesToReconcile.map((invoice) => invoice.id);
    const attemptKey = buildReconciliationAttemptKey(`pending-balance|${leaseId}`, postingsSelected, invoiceIds);
    try {
      const result = await mutation<MutationStatus>(reconcilePendingBalanceWithInvoicesMutation, {
        input: {
          postingsSelected: JSON.stringify(postingsSelected.map(({ id, amountSelected }) => ({ id, amountSelected }))),
          allTransactionsOfLease: JSON.stringify(
            allTransactionsOfLease.map(({ id, amountToBeReconciled }) => ({ id, amountToBeReconciled }))
          ),
          invoicesToReconcile: invoiceIds,
          leaseId,
          clientContactId,
          decisionId: takeDecisionId(attemptKey),
        },
      });
      if (result.status) {
        forgetPendingDecisionId(attemptKey);
      }
      return result.status ?? false;
    } catch (error) {
      console.error('Error reconciling pending balance with invoices', error);
      return false;
    }
  };

  const snoozeTransaction = async (transactionId: string, snoozedUntil: string | null) => {
    const attemptKey = [transactionId, 'snooze', snoozedUntil ?? 'wake'].join('|');
    const result = await mutation<MutationStatus>(snoozeTransactionMutation, {
      input: {
        transactionId,
        snoozedUntil,
        decisionId: takeDecisionId(attemptKey),
      },
    });
    forgetPendingDecisionId(attemptKey);
    return result.status ?? false;
  };

  const reverseTransaction = async (transactionId: string) => {
    const attemptKey = [transactionId, 'reverse'].join('|');
    try {
      const result = await mutation<MutationStatus>(reverseTransactionMutation, {
        input: {
          transactionId,
          decisionId: takeDecisionId(attemptKey),
        },
      });
      if (result.status) {
        forgetPendingDecisionId(attemptKey);
      }
      return result.status ?? false;
    } catch (error) {
      console.error('Error reversing transaction', error);
      return false;
    }
  };

  const unreconcileTransactionOrInvoices = async (
    transaction: Transaction,
    originalTransactions: Transaction[],
    transactionInvoices?: Invoice[],
    leaseId?: string
  ) => {
    const attemptKey = buildUnreconciliationAttemptKey(
      transaction.id,
      originalTransactions,
      transactionInvoices,
      leaseId
    );
    try {
      const result = await mutation<MutationStatus>(unreconcileTransactionOrInvoicesMutation, {
        input: {
          transaction: JSON.stringify(transaction),
          originalTransactions: JSON.stringify(await withFreshOffsetCounterparts(transaction, originalTransactions)),
          transactionInvoices: transactionInvoices ? JSON.stringify(transactionInvoices) : undefined,
          leaseId,
          decisionId: takeDecisionId(attemptKey),
        },
      });
      if (result.status) {
        forgetPendingDecisionId(attemptKey);
      }
      return result.status ?? false;
    } catch (error) {
      console.error('Error unreconciling transaction or invoices', error);
      return false;
    }
  };

  const writeOffPendingBalance = async (leaseId: string) => {
    try {
      const result = await mutation<MutationStatus>(writeOffPendingBalanceMutation, {
        input: {
          leaseId,
          clientContactId,
        },
      });
      return result.status ?? false;
    } catch (error) {
      console.error('Error writing off pending balance', error);
      return false;
    }
  };

  const editPrivateAccountTransactionAmount = async (transactionId: string, invoiceId: string, amount: number) => {
    try {
      const result = await mutation<MutationStatus>(editPrivateAccountTransactionAmountMutation, {
        input: {
          transactionId,
          invoiceId,
          amount,
        },
      });
      return result.status ?? false;
    } catch {
      return false;
    }
  };

  return (
    <ReconciliationContext.Provider
      value={{
        changeTransactionLinks,
        reconcileTransactionWithTransaction,
        reconcileTransactionWithInvoices,
        reconcileInvoicesWithCreditNotes,
        reconcilePendingBalanceWithInvoices,
        reverseTransaction,
        snoozeTransaction,
        unreconcileTransactionOrInvoices,
        writeOffPendingBalance,
        editPrivateAccountTransactionAmount,
      }}
    >
      {children}
    </ReconciliationContext.Provider>
  );
};

export const useReconciliationContext = (): ReconciliationContextType => {
  const context = useContext(ReconciliationContext);
  if (!context) {
    throw new Error('useReconciliationContext must be used within a ReconciliationProvider');
  }
  return context;
};
